import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import {
  CheckCircle,
  ChevronDown,
  CirclePlus,
  Copy,
  ExternalLink,
  Loader2,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

import { GuarantorDetails } from "js/components/Tenants/TenantReportDetails/typing";
import {
  DescriptionList,
  DescriptionItem,
} from "js/components/ui/description-list";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "js/components/ui/collapsible";
import { useUpdateGuarantor } from "../../../../hooks/useGuarantors";
import { useAddGuarantorInvestigation } from "../../../../hooks/useTenantReports";
import { toTitleCase } from "../../../../utils/utils";
import { toast } from "../../../../utils/toast";
import PersonEditModal from "./PersonEditModal";

interface GuarantorDetailsComponentProps {
  guarantor: GuarantorDetails;
  onUpdate: (updatedGuarantor: GuarantorDetails) => void;
  folio?: string;
  isEditable: boolean;
  isModalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
  reportId?: string;
  reportStatus?: string;
  guarantorRequired?: boolean;
  guarantorInvestigationRequired?: boolean;
  guarantorUpgradeStatus?: "Pending Payment" | "Paid" | null;
  paymentUrl?: string | null;
}

const GuarantorDetailsComponent: React.FC<GuarantorDetailsComponentProps> = ({
  guarantor,
  onUpdate,
  folio,
  isEditable,
  isModalOpen,
  onModalOpenChange,
  reportId,
  reportStatus,
  guarantorRequired,
  guarantorInvestigationRequired,
  guarantorUpgradeStatus,
  paymentUrl,
}) => {
  const [isLegalPerson, setIsLegalPerson] = useState<boolean>(false);
  const isModalVisible = isModalOpen ?? false;
  const setIsModalVisible = onModalOpenChange ?? (() => {});

  const [pendingInvestigation, setPendingInvestigation] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const updateGuarantorMutation = useUpdateGuarantor();
  const addGuarantorInvestigationMutation = useAddGuarantorInvestigation();

  const showAddInvestigationButton =
    guarantorRequired === true &&
    guarantorInvestigationRequired === false &&
    guarantorUpgradeStatus == null &&
    reportId &&
    (reportStatus === "In Progress" || reportStatus === "in_progress");

  const showPendingPayment = guarantorUpgradeStatus === "Pending Payment";

  const isGuarantorDataComplete = (g: GuarantorDetails) =>
    !!g.name && !!g.first_surname && !!g.email && g.phone_number != null;

  const handleCopyPaymentLink = () => {
    if (paymentUrl) {
      navigator.clipboard
        .writeText(paymentUrl)
        .then(() => toast.success("Enlace de pago copiado"))
        .catch(() => toast.error("No se pudo copiar el enlace"));
    }
  };

  const handleConfirmPayment = () => {
    addGuarantorInvestigationMutation.mutate(reportId!, {
      onSuccess: (data) => {
        const url: string = data.payment_url;
        if (url) {
          window.location.href = url;
        }
      },
    });
  };

  const handleAddInvestigation = () => {
    if (!isGuarantorDataComplete(guarantor)) {
      toast.info(
        "Completa los datos del fiador antes de agregar la investigación.",
      );
      setPendingInvestigation(true);
      setIsModalVisible(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  useEffect(() => {
    setIsLegalPerson(guarantor.legal_type == 2);
  }, [guarantor]);

  const handleCancel = () => {
    setIsModalVisible(false);
    setPendingInvestigation(false);
  };

  const handleOk = (values: any) => {
    const updatedGuarantorData = {
      ...guarantor,
      name: values.name,
      first_surname: values.first_surname,
      second_surname: values.second_surname,
      email: values.email,
      phone_number: values.phone_number,
      dial_code: values.dial_code,
      rfc: values.rfc,
      curp: values.curp,
      birth_date: values.birth_date,
      legal_type: isLegalPerson ? 2 : 1,
    } as GuarantorDetails;

    const queryParams = folio ? { folio } : undefined;

    updateGuarantorMutation.mutate(
      {
        guarantor: {
          ...updatedGuarantorData,
          legal_type: isLegalPerson ? 2 : 1,
          phone_number: String(updatedGuarantorData.phone_number),
          dial_code: updatedGuarantorData.dial_code ?? "",
        },
        guarantorId: guarantor?.id ?? "",
        queryParams,
      },
      {
        onSuccess: () => {
          onUpdate(updatedGuarantorData);
          setIsModalVisible(false);

          if (
            pendingInvestigation &&
            isGuarantorDataComplete(updatedGuarantorData)
          ) {
            setPendingInvestigation(false);
            setTimeout(() => setConfirmDialogOpen(true), 300);
          }
        },
      },
    );
  };

  const fullName = guarantor?.name
    ? toTitleCase(
        `${guarantor.name} ${guarantor.first_surname ?? ""} ${guarantor.second_surname ?? ""}`.trim(),
      )
    : null;

  return (
    <>
      {/* Upsell: collapsible compact banner */}
      {showAddInvestigationButton && (
        <Collapsible className="mb-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50/60">
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm group">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="size-4 shrink-0 text-blue-600" />
                <span className="font-medium text-blue-900 truncate">
                  Refuerza esta renta con verificación del fiador
                </span>
                <Badge className="hidden sm:inline-flex bg-blue-100 text-blue-700 border-blue-200 text-[10px] shrink-0">
                  Desde {isLegalPerson ? "$499" : "$299"}
                </Badge>
              </div>
              <ChevronDown className="size-4 shrink-0 text-blue-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t border-blue-200 px-3 pb-3 pt-2.5">
                <p className="mb-2.5 text-xs text-blue-800/80">
                  Suma el <strong>análisis legal y crediticio del fiador</strong>{" "}
                  a la investigación del inquilino.
                </p>
                <ul className="mb-3 space-y-1">
                  <li className="flex items-center gap-1.5 text-xs text-blue-700/70">
                    <CheckCircle className="size-3 text-blue-500 shrink-0" />
                    Antecedentes legales a nivel nacional
                  </li>
                  <li className="flex items-center gap-1.5 text-xs text-blue-700/70">
                    <CheckCircle className="size-3 text-blue-500 shrink-0" />
                    Historial en Buró de Crédito
                  </li>
                  <li className="flex items-center gap-1.5 text-xs text-blue-700/70">
                    <CheckCircle className="size-3 text-blue-500 shrink-0" />
                    Listas negras (SAT, FGR, etc.)
                  </li>
                </ul>
                <Button
                  size="sm"
                  onClick={handleAddInvestigation}
                  disabled={addGuarantorInvestigationMutation.isPending}
                  className="w-full sm:w-auto"
                >
                  {addGuarantorInvestigationMutation.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <CirclePlus className="size-3.5" />
                  )}
                  Agregar Verificación — {isLegalPerson ? "$499" : "$299"} IVA
                  incl.
                </Button>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Pending payment: compact inline banner */}
      {showPendingPayment && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-yellow-200 bg-yellow-50/60 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <TriangleAlert className="size-4 shrink-0 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-900 truncate">
              Investigación del fiador
            </span>
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-[10px] shrink-0">
              Pago Pendiente
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            {paymentUrl && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 text-xs"
                  onClick={() => {
                    window.location.href = paymentUrl;
                  }}
                >
                  <ExternalLink className="size-3" />
                  Pagar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={handleCopyPaymentLink}
                >
                  <Copy className="size-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <DescriptionList>
        <DescriptionItem
          label={`Nombre${isLegalPerson ? " del representante legal" : ""}`}
          copyable
        >
          {fullName}
        </DescriptionItem>
        <DescriptionItem label="Correo" copyable>
          {guarantor.email ?? null}
        </DescriptionItem>
        <DescriptionItem label="Teléfono" copyable>
          {guarantor.phone_number
            ? `${guarantor?.dial_code ?? ""} ${guarantor.phone_number}`
            : null}
        </DescriptionItem>
        <DescriptionItem label="RFC" copyable>
          {guarantor.rfc ?? null}
        </DescriptionItem>
        <DescriptionItem label="Fecha de Nacimiento">
          {guarantor?.birth_date
            ? dayjs(guarantor.birth_date).format("DD/MM/YYYY")
            : null}
        </DescriptionItem>
        <DescriptionItem label="CURP" copyable>
          {guarantor.curp ?? null}
        </DescriptionItem>
      </DescriptionList>

      <PersonEditModal
        isVisible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleOk}
        personData={guarantor}
        personType="guarantor"
        loading={updateGuarantorMutation.isPending}
      />

      {/* Payment confirmation dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Verificación Integral del Fiador
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  Estás por agregar la verificación integral del fiador.
                </p>
                <div className="flex items-center justify-between rounded-lg bg-muted px-3.5 py-2.5 mb-3">
                  <span className="text-sm font-semibold text-foreground">
                    {isLegalPerson ? "Persona moral" : "Persona física"}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {isLegalPerson ? "$499" : "$299"}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      IVA incluido
                    </span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Al continuar serás redirigido a la página de pago.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPayment}>
              Continuar al pago
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuarantorDetailsComponent;
